import type { CreateAttachmentDto, StageActionDto } from "../types/MainType.type";
import { API_BASE_URL, getHeaders, type FetchParams } from "./NetworkService";

export interface GenericFetchResponse<T> {
  data: T[];
  totalItems: number;
}

export type FilterPredicate<T> = (item: T, params: FetchParams) => boolean;

// ---------------------------------------------------------------------------
// 1. Office User Form Operations (Form Controllers)
// ---------------------------------------------------------------------------

/**
 * Fetch Applications List
 * GET /:endpoint
 */
export const fetchApplications = async <T>(
  endpoint: string,
  params: FetchParams,
  mapFn: (item: any) => T,
  filterFn?: FilterPredicate<T>
): Promise<GenericFetchResponse<T>> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "အချက်အလက်များ ရယူ၍ မရပါ");
  }

  const rawData = await response.json();
  const mappedData: T[] = Array.isArray(rawData) ? rawData.map(mapFn) : [];

  const filteredData = filterFn
    ? mappedData.filter((item) => filterFn(item, params))
    : mappedData;

  const { currentPage, itemsPerPage } = params;
  const offset = (currentPage - 1) * itemsPerPage;

  return {
    data: filteredData.slice(offset, offset + itemsPerPage),
    totalItems: filteredData.length,
  };
};

/**
 * Fetch Single Document by ID
 * GET /:endpoint/:id
 */
export const fetchApplicationById = async <T>(
  endpoint: string,
  id: string,
  mapFn: (item: any) => T
): Promise<T> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}/${id}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "အချက်အလက်များ ရယူ၍ မရပါ");
  }

  const rawData = await response.json();
  return mapFn(rawData);
};

/**
 * Save Draft (POST for New, PUT for Existing Update)
 * POST /:endpoint/draft OR PUT /:endpoint/:id/draft
 */
export const saveDraft = async <T>(
  endpoint: string,
  id: string | null,
  data: Partial<T>
): Promise<any> => {
  // Fixed path order: /draft/${id} instead of /${id}/draft
  const url = id
    ? `${API_BASE_URL}/${endpoint}/draft/${id}`
    : `${API_BASE_URL}/${endpoint}/draft`;

  const response = await fetch(url, {
    method: id ? "PUT" : "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ"
    );
  }

  return response.json();
};

/**
 * Submit Document (Form submission includes signature fields inside the DTO)
 * POST /:endpoint/:id/submit
 */
export const submitForm = async <T>(
  endpoint: string,
  id: string,
  data: T
): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}/submit/${id}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "တင်ပြခြင်း မအောင်မြင်ပါ");
  }

  return response.json();
};

/**
 * Soft Delete Document
 * DELETE /:endpoint/:id
 */
export const deleteForm = async (
  endpoint: string,
  id: string
): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/${endpoint}/${id}`, {
    method: "DELETE",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to delete document with ID ${id}`
    );
  }

  return response.json();
};

// ---------------------------------------------------------------------------
// 2. Superior / Reviewer Stage Actions (Global DocumentController)
// Endpoint root is 'documents' and uses PATCH
// ---------------------------------------------------------------------------

/**
 * Approve Document (Superior Action - No Signatures Required)
 * PATCH /documents/:id/approve
 */
export const startReviewForm = async (
  id: string,
): Promise<any> => {

  const response = await fetch(`${API_BASE_URL}/documents/${id}/under-review`, {
    method: "PATCH",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "စိစစ်မှုစတင်ခြင်း မအောင်မြင်ပါ"
    );
  }

  return response.json();
};

export const attachForm = async (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
): Promise<any> => {
  const dto: StageActionDto = {
    comments,
    attachments,
    targetGeoLevels,
  };

  const response = await fetch(`${API_BASE_URL}/documents/${id}/attach`, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify(dto),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "ဖိုင်တွဲ သိမ်းဆည်းခြင်း မအောင်မြင်ပါ");
  }

  return response.json();
};

export const forwardDocument = async (
  id: string,
  targetGeoLevels: string[],
  message?: string
): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/documents/${id}/forward`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ targetGeoLevels, message }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "အဆင့်မြင့်ရုံးသို့ ပေးပို့ခြင်း မအောင်မြင်ပါ");
  }

  return response.json();
};

export const submitAttachmentAction = async (
  id: string,
  comments: string | undefined,
  attachments: CreateAttachmentDto[],
  targetGeoLevels: string[],
  forwardGeoLevels: string[]
): Promise<any> => {
  if (forwardGeoLevels.length > 0) {
    await attachForm(id, comments, attachments, targetGeoLevels);
    return forwardDocument(id, forwardGeoLevels, comments);
  }

  return attachForm(id, comments, attachments, targetGeoLevels);
};

const getUploadHeaders = (): HeadersInit => {
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const uploadFile = async (
  file: File
): Promise<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: "POST",
    headers: getUploadHeaders(), // <-- Uses getUploadHeaders without 'Content-Type'
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "ဖိုင်တင်သွင်းခြင်း မအောင်မြင်ပါ");
  }

  return response.json();
};

/**
 * Return Document (Request Revision)
 * PATCH /documents/:id/return
 */
// export const returnForm = async (
//   id: string,
//   comments?: string,
//   attachments?: CreateAttachmentDto[]
// ): Promise<any> => {
//   const dto: StageActionDto = {
//     comments,
//     attachments,
//   };

//   const response = await fetch(`${API_BASE_URL}/documents/${id}/return`, {
//     method: "PATCH",
//     headers: getHeaders(),
//     body: JSON.stringify(dto),
//   });

//   if (!response.ok) {
//     const errorData = await response.json().catch(() => ({}));
//     throw new Error(
//       errorData.message || "ပြင်ဆင်ရန် ပြန်လည်ပေးပို့ခြင်း မအောင်မြင်ပါ"
//     );
//   }

//   return response.json();
// };

/**
 * Reject Document
 * PATCH /documents/:id/reject
 */
// export const rejectForm = async (
//   id: string,
//   remark?: string
// ): Promise<any> => {
//   const dto: StageActionDto = { remark };

//   const response = await fetch(`${API_BASE_URL}/documents/${id}/reject`, {
//     method: "PATCH",
//     headers: getHeaders(),
//     body: JSON.stringify(dto),
//   });

//   if (!response.ok) {
//     const errorData = await response.json().catch(() => ({}));
//     throw new Error(errorData.message || "ငြင်းပယ်ခြင်း မအောင်မြင်ပါ");
//   }

//   return response.json();
// };

//search
export const searchApplications = async <T>(
  endpoint: string,
  params: Record<string, any>,
  mapFn: (item: any) => T
): Promise<GenericFetchResponse<T>> => {
  // 1. Build Query Parameters string (e.g. ?status=UNDER_REVIEW&personName=ဦးအေး)
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/${endpoint}${queryString ? `?${queryString}` : ''}`;

  // 2. Fetch filtered data from backend
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "အချက်အလက်များ ရယူ၍ မရပါ");
  }

  const result = await response.json();

  // 3. Handle response (supports both paginated object and plain array response)
  if (result && Array.isArray(result.data)) {
    return {
      data: result.data.map(mapFn),
      totalItems: result.totalItems ?? result.data.length,
    };
  }

  const mappedData: T[] = Array.isArray(result) ? result.map(mapFn) : [];
  return {
    data: mappedData,
    totalItems: mappedData.length,
  };
};